import React, {ChangeEvent, useMemo, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBinoculars,
    faCalendarDays,
    faCrow,
    faLocationDot,
    faMapLocationDot
} from "@fortawesome/free-solid-svg-icons";
import {EBirdChecklistByFamily, EBirdChecklistBySpecies, EBirdObservationsBySpecies} from "ebird-mydata-reader";
import {ChecklistCard} from "../../components/checklist-card";
import {formatDate} from "../../helpers/helpers";
import {toRootRelativeImagePath} from "../../helpers/imagePaths";
import {useNavigate} from "react-router-dom";
import {getBirdLifeFamilySummary, getBirdLifeSpeciesData, redListCategoryLabels} from "../../helpers/birdlifeData";
import {
    ChecklistFilter,
    getFamilyStats,
    getImageAttributionUrl
} from "./checklist-helpers";
import './checklist-family-detail.scss';

export type ChecklistFamilyDetailProps = {
    family: EBirdChecklistByFamily,
    observationsBySpecies: EBirdObservationsBySpecies[],
    onChangeSpecies: (species: EBirdChecklistBySpecies) => void
}

export function ChecklistFamilyDetail({family, observationsBySpecies, onChangeSpecies}: ChecklistFamilyDetailProps) {
    const navigate = useNavigate();
    const [activeSpeciesFilter, setActiveSpeciesFilter] = useState<ChecklistFilter>('all');
    const [activeStatusCategory, setActiveStatusCategory] = useState<string>();
    const [speciesSearchTerm, setSpeciesSearchTerm] = useState('');
    const selectedFamilyStats = useMemo(() => getFamilyStats(family), [family]);
    const conservationSummary = useMemo(() => getBirdLifeFamilySummary(family), [family]);
    const statusEntries = useMemo(() => {
        const categoryOrder = ['LC', 'VU', 'NT', 'EN', 'CR', 'DD', 'EW', 'EX'];
        const entries = Object.entries(conservationSummary.categories);

        return entries.sort(([a], [b]) => {
            const aIndex = categoryOrder.indexOf(a);
            const bIndex = categoryOrder.indexOf(b);
            if (aIndex >= 0 && bIndex >= 0) return aIndex - bIndex;
            if (aIndex >= 0) return -1;
            if (bIndex >= 0) return 1;
            return a.localeCompare(b);
        });
    }, [conservationSummary.categories]);
    const observationCountBySpecies = useMemo(() => {
        const counts = new Map<number, number>();

        for (const checklistSpecies of family.speciesList) {
            const taxonomicOrders = new Set<number>([
                checklistSpecies.taxonomicOrder,
                ...(checklistSpecies.alternateTaxonomicOrders ?? [])
            ]);
            const count = observationsBySpecies
                .filter(species => taxonomicOrders.has(species.taxonomicOrder))
                .reduce((total, species) => total + species.observations.length, 0);

            counts.set(checklistSpecies.taxonomicOrder, count);
        }

        return counts;
    }, [family, observationsBySpecies]);
    const visibleSpecies = useMemo(() => {
        const normalizedSearch = speciesSearchTerm.trim().toLowerCase();

        return family.speciesList.filter(species => {
            if (activeSpeciesFilter === 'seen' && (!species.seen || species.isExtinct)) return false;
            if (activeSpeciesFilter === 'unseen' && (species.seen || species.isExtinct)) return false;
            if (activeStatusCategory && getBirdLifeSpeciesData(species)?.redListCategory !== activeStatusCategory) return false;
            if (!normalizedSearch) return true;

            return `${species.commonName} ${species.scientificName}`.toLowerCase().includes(normalizedSearch);
        });
    }, [activeSpeciesFilter, activeStatusCategory, family, speciesSearchTerm]);

    const handleSpeciesSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSpeciesSearchTerm(event.target.value);
    }

    const getCategoryClassName = (category: string) => {
        return [
            'checklist-family-status__badge',
            `checklist-family-status__badge--${category.toLowerCase()}`,
            activeStatusCategory === category ? 'is-active' : ''
        ].filter(Boolean).join(' ');
    }

    const getSpeciesDescription = (species: EBirdChecklistBySpecies) => {
        const firstObservation = species.firstObservation;
        const observationCount = observationCountBySpecies.get(species.taxonomicOrder) ?? 0;
        const locationBits = firstObservation
            ? [firstObservation.location, firstObservation.county, firstObservation.stateProvince].filter(Boolean)
            : [];

        return <>
            {species.seen ?
                <>
                    <span className="family-card__detail-row">
                        <FontAwesomeIcon icon={faBinoculars} aria-hidden="true" />
                        <span>Seen {observationCount.toLocaleString()} {observationCount === 1 ? 'time' : 'times'}</span>
                    </span>
                    <span className="family-card__detail-row">
                        <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                        <span><strong className="family-card__detail-heading">First Seen:</strong> {locationBits.join(', ')}</span>
                    </span>
                    <span className="family-card__detail-row">
                        <FontAwesomeIcon icon={faCalendarDays} aria-hidden="true" />
                        <span>{formatDate(firstObservation.date)}</span>
                    </span>
                    <button
                        type="button"
                        className="family-card__map-cta"
                        onClick={() => navigate(`/map?year=life&mode=species&species=${species.speciesCode}`)}
                    >
                        Show my sightings on a map
                        <FontAwesomeIcon icon={faMapLocationDot} aria-hidden="true" />
                    </button>
                </> :
                <>
                    <span className="family-card__detail-row">
                        <FontAwesomeIcon icon={faCrow} aria-hidden="true" />
                        <span>Not seen yet</span>
                    </span>
                </>}
        </>;
    }

    return (
        <>
            {conservationSummary.matchedSpecies ?
                <section className="checklist-family-status" aria-labelledby="family-species-status-title">
                    <div className="checklist-family-status__header">
                        <h2 id="family-species-status-title">Species Status</h2>
                        {activeStatusCategory ?
                            <button type="button" onClick={() => setActiveStatusCategory(undefined)}>
                                (Reset)
                            </button>
                            : null
                        }
                    </div>
                    <ul aria-label="Red List category breakdown">
                        {statusEntries.map(([category, count]) => (
                            <li className={getCategoryClassName(category)} key={category}>
                                <button
                                    type="button"
                                    aria-pressed={activeStatusCategory === category}
                                    onClick={() => setActiveStatusCategory(activeStatusCategory === category ? undefined : category)}
                                >
                                    <span>{category}</span>
                                    <strong>{count.toLocaleString()}</strong>
                                    <small>{redListCategoryLabels[category] ?? category}</small>
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
                : null
            }

            <div className="checklist__toolbar">
                <div className="checklist__tabs" aria-label="Species filters">
                    <button
                        type="button"
                        className={`checklist__tab ${activeSpeciesFilter === 'all' ? 'is-active' : ''}`}
                        onClick={() => setActiveSpeciesFilter('all')}
                    >
                        All <span className="checklist__tab-optional">Species </span>({selectedFamilyStats.totalSpecies.toLocaleString()})
                    </button>
                    <button
                        type="button"
                        className={`checklist__tab ${activeSpeciesFilter === 'seen' ? 'is-active' : ''}`}
                        onClick={() => setActiveSpeciesFilter('seen')}
                    >
                        Seen ({selectedFamilyStats.seenSpecies.toLocaleString()})
                    </button>
                    <button
                        type="button"
                        className={`checklist__tab ${activeSpeciesFilter === 'unseen' ? 'is-active' : ''}`}
                        onClick={() => setActiveSpeciesFilter('unseen')}
                    >
                        Not Seen ({selectedFamilyStats.unseenSpecies.toLocaleString()})
                    </button>
                </div>
                <label className="checklist__search">
                    <span>Search species</span>
                    <input
                        type="search"
                        value={speciesSearchTerm}
                        onChange={handleSpeciesSearchChange}
                        placeholder="Search species..."
                    />
                </label>
            </div>

            {visibleSpecies.length ?
                <ul className="checklist-list">
                    {visibleSpecies.map(species => (
                        <li className="checklist-list__item" key={species.scientificName}>
                            <ChecklistCard
                                subject={species}
                                onClick={onChangeSpecies}
                                seen={species.seen}
                                title={species.commonName}
                                subtitle={species.scientificName}
                                description={getSpeciesDescription(species)}
                                statusLabel={species.isExtinct ? 'Extinct' : undefined}
                                statusVariant={species.isExtinct ? 'extinct' : undefined}
                                img={toRootRelativeImagePath(species.image?.medium ?? species.image?.thumb) || '/assets/fallback-bird.svg'}
                                attributionUrl={getImageAttributionUrl(species.image)}
                                alt={species.commonName}
                            />
                        </li>
                    ))}
                </ul> :
                <div className="checklist__empty-state">
                    <strong>No species found</strong>
                    <span>Try a different search or switch species filters.</span>
                </div>
            }
        </>
    );
}
